import React, { useState, useRef, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Tooltip,
  Skeleton,
  Collapse,
  Box,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Person from "@mui/icons-material/Person";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";

// Constants
const SORT_STATES = {
  NONE: "none",
  ASC: "asc",
  DESC: "desc",
};

const ACTIONS_COLUMN_ID = "actions";
const LOCALIZABLE_CELL_VALUES = new Set(["Not Assigned", "N/A"]);

const translateCellValue = (value, t) => {
  if (typeof value !== "string") return value;
  return LOCALIZABLE_CELL_VALUES.has(value) ? t(value) : value;
};

// Styled Components
const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: "20px",
  overflow: "auto",
  borderRadius: "10px",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#647490",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#B7BFCC",
    borderRadius: "10px",
    margin: "5px",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#647490 #B7BFCC",
}));

const ResponsiveTableCell = styled(TableCell)(({ theme }) => ({
  padding: "8px 12px",
  minWidth: "120px",
  maxWidth: "300px",
  [theme.breakpoints.down("sm")]: {
    padding: "6px 8px",
    fontSize: "0.75rem",
    minWidth: "80px",
    maxWidth: "150px",
  },
}));

const SortableHeaderCell = styled(ResponsiveTableCell)({
  cursor: "pointer",
  userSelect: "none",
  position: "relative",
  "&:hover": {
    backgroundColor: "#586280",
  },
  transition: "background-color 0.2s ease",
});

const SortIconsContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginLeft: "8px",
  gap: "0px",
});

const SortIcon = styled("div")(({ active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "16px",
  height: "16px",
  opacity: active ? 1 : 0.5,
  transition: "opacity 0.2s ease",
}));

const HeaderContent = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  fontWeight: 600,
});

const ProfileImage = styled("img")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  marginRight: "8px",
  border: "2px solid #183059",
  verticalAlign: "middle",
  objectFit: "cover",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "32px",
    height: "32px",
    marginRight: "6px",
  },
}));

const ProfileIcon = styled(Person)(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  marginRight: "8px",
  border: "2px solid #183059",
  verticalAlign: "middle",
  color: "#647490",
  backgroundColor: "#f5f5f5",
  padding: "4px",
  flexShrink: 0,
  [theme.breakpoints.down("sm")]: {
    width: "32px",
    height: "32px",
    marginRight: "6px",
  },
}));

const ExpandedRowContent = styled(Box)(({ theme }) => ({
  padding: "20px",
  backgroundColor: "#f8f9fa",
  borderTop: "1px solid #e0e0e0",
  borderBottom: "1px solid #e0e0e0",
}));

const DetailGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const DetailItem = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const DetailLabel = styled("span")({
  fontSize: "0.75rem",
  color: "#647490",
  fontWeight: 600,
  textTransform: "uppercase",
});

const DetailValue = styled("span")({
  fontSize: "0.9rem",
  color: "#183059",
  fontWeight: 400,
});

const ActionsContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

// Main Component
export default function SchoolDynamicTable({
  headers = [],
  data = [],
  loading = false,
  skeletonRowCount = 5,
  columnWidths = {},
  renderExpandedRow,
  onDelete,
  expandedRow,
  onRowClick,
  profileImageColumn = null, // specify which column has profile images
  showEyeIcon = false, // whether to show eye icon for Monitor
  onEyeIconClick = null, // callback for eye icon click
}) {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: SORT_STATES.NONE,
  });

  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Sort handler
  const handleSort = useCallback((headerKey) => {
    if (headerKey === ACTIONS_COLUMN_ID) return;

    setSortConfig((prev) => {
      if (prev.key !== headerKey) {
        return { key: headerKey, direction: SORT_STATES.ASC };
      }
      switch (prev.direction) {
        case SORT_STATES.ASC:
          return { key: headerKey, direction: SORT_STATES.DESC };
        case SORT_STATES.DESC:
          return { key: headerKey, direction: SORT_STATES.NONE };
        default:
          return { key: headerKey, direction: SORT_STATES.ASC };
      }
    });
  }, []);

  // Sorted data
  const sortedData = useMemo(() => {
    if (sortConfig.direction === SORT_STATES.NONE || !sortConfig.key) {
      return data;
    }

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (React.isValidElement(aValue)) {
        aValue = aValue.props?.alt || aValue.props?.title || "";
      }
      if (React.isValidElement(bValue)) {
        bValue = bValue.props?.alt || bValue.props?.title || "";
      }

      aValue = aValue == null ? "" : String(aValue).toLowerCase();
      bValue = bValue == null ? "" : String(bValue).toLowerCase();

      const aNum = parseFloat(aValue);
      const bNum = parseFloat(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === SORT_STATES.ASC
          ? aNum - bNum
          : bNum - aNum;
      }

      return sortConfig.direction === SORT_STATES.ASC
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [data, sortConfig]);

  const handleRowClickInternal = useCallback(
    (rowIndex) => {
      if (onRowClick) {
        onRowClick(rowIndex);
      }
    },
    [onRowClick],
  );

  const handleDelete = useCallback(
    (e, row, rowIndex) => {
      e.stopPropagation();
      if (onDelete) {
        onDelete(row, rowIndex);
      } else {
        console.log("Delete clicked for row:", row);
      }
    },
    [onDelete],
  );

  const isActionsColumn = (header) => header.key === ACTIONS_COLUMN_ID;
  const showActionsColumn = Boolean(
    onRowClick || renderExpandedRow || showEyeIcon,
  );
  const filteredHeaders = showActionsColumn
    ? headers
    : headers.filter((header) => !isActionsColumn(header));

  // Render functions
  const renderSortIcons = (headerKey) => {
    const isCurrentSort = sortConfig.key === headerKey;
    const sortDirection = isCurrentSort
      ? sortConfig.direction
      : SORT_STATES.NONE;

    return (
      <SortIconsContainer>
        <SortIcon active={sortDirection === SORT_STATES.ASC}>
          <span style={{ fontSize: "10px" }}>▲</span>
        </SortIcon>
        <SortIcon active={sortDirection === SORT_STATES.DESC}>
          <span style={{ fontSize: "10px" }}>▼</span>
        </SortIcon>
      </SortIconsContainer>
    );
  };

  const renderDefaultExpandedContent = (row) => (
    <ExpandedRowContent>
      <DetailGrid>
        {filteredHeaders
          .filter((h) => !isActionsColumn(h))
          .map((header) => (
            <DetailItem key={header.key}>
              <DetailLabel>{t(header.value)}</DetailLabel>
              <DetailValue>{translateCellValue(row[header.key], t) || ""}</DetailValue>
            </DetailItem>
          ))}
      </DetailGrid>
    </ExpandedRowContent>
  );

  const renderSkeletonRows = () =>
    Array(skeletonRowCount)
      .fill(0)
      .map((_, rowIndex) => (
        <TableRow
          key={`skeleton-${rowIndex}`}
          sx={{
            height: "50px",
            "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
            borderBottom: "1px solid #ddd",
          }}
        >
          {filteredHeaders.map((header, colIndex) => (
            <ResponsiveTableCell
              key={`skeleton-${rowIndex}-${colIndex}`}
              align="left"
              sx={{
                width: isActionsColumn(header)
                  ? "100px"
                  : columnWidths[header.key] || "auto",
                padding: isActionsColumn(header) ? "0 8px" : "8px 12px",
              }}
            >
              {isActionsColumn(header) ? (
                <ActionsContainer>
                  {showEyeIcon && (
                    <Skeleton variant="circular" width={24} height={24} />
                  )}
                  <Skeleton variant="circular" width={24} height={24} />
                </ActionsContainer>
              ) : (
                <Skeleton
                  variant="text"
                  width={`${60 + Math.random() * 40}%`}
                />
              )}
            </ResponsiveTableCell>
          ))}
        </TableRow>
      ));

  return (
    <ResponsiveTableContainer ref={tableContainerRef}>
      <Table
        sx={{
          tableLayout: "auto",
          width: "100%",
          minWidth: isSmallScreen ? "100%" : "800px",
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#647490" }}>
            {filteredHeaders.map((header, index) => (
              <SortableHeaderCell
                key={header.key || index}
                sx={{
                  backgroundColor: "#647490",
                  color: "white",
                  fontWeight: "bold",
                  textAlign: "left",
                  borderTopLeftRadius: index === 0 ? "10px" : 0,
                  borderTopRightRadius:
                    index === filteredHeaders.length - 1 ? "10px" : 0,
                  borderBottom: "none",
                  whiteSpace: "normal",
                  width: isActionsColumn(header)
                    ? "100px"
                    : columnWidths[header.key] || "auto",
                }}
                onClick={() => handleSort(header.key)}
              >
                {loading ? (
                  <Skeleton width="80%" />
                ) : (
                  <HeaderContent>
                    {t(header.value)}
                    {!isActionsColumn(header) && renderSortIcons(header.key)}
                  </HeaderContent>
                )}
              </SortableHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            renderSkeletonRows()
          ) : sortedData.length > 0 ? (
            sortedData.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <TableRow
                  id={row.fullData?.id != null ? `driver-row-${row.fullData.id}` : undefined}
                  sx={{
                    height: "50px",
                    cursor: "pointer",
                    "&:nth-of-type(odd)": {
                      backgroundColor:
                        expandedRow === rowIndex ? "#e3f2fd" : "#f5f5f5",
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor:
                        expandedRow === rowIndex ? "#e3f2fd" : "#ffffff",
                    },
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                    borderBottom:
                      expandedRow === rowIndex ? "none" : "1px solid #ddd",
                  }}
                  onClick={() => handleRowClickInternal(rowIndex)}
                >
                  {filteredHeaders.map((header, colIndex) => (
                    <ResponsiveTableCell
                      key={`${rowIndex}-${header.key || colIndex}`}
                      align="left"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: isActionsColumn(header)
                          ? "100px"
                          : columnWidths[header.key] || "auto",
                        padding: isActionsColumn(header) ? "0 8px" : "8px 12px",
                      }}
                    >
                      {isActionsColumn(header) ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {showEyeIcon && (
                            <Box
                              sx={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "20px",
                                padding: "8px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onEyeIconClick) {
                                    onEyeIconClick(row, rowIndex);
                                  } else {
                                    handleRowClickInternal(rowIndex);
                                  }
                                }}
                                color="error"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M13.125 10C13.125 10.8288 12.7958 11.6237 12.2097 12.2097C11.6237 12.7958 10.8288 13.125 10 13.125C9.1712 13.125 8.37634 12.7958 7.79029 12.2097C7.20424 11.6237 6.875 10.8288 6.875 10C6.875 9.1712 7.20424 8.37634 7.79029 7.79029C8.37634 7.20424 9.1712 6.875 10 6.875C10.8288 6.875 11.6237 7.20424 12.2097 7.79029C12.7958 8.37634 13.125 9.1712 13.125 10Z"
                                    fill="#20A8E0"
                                  />
                                  <path
                                    d="M0 10C0 10 3.75 3.125 10 3.125C16.25 3.125 20 10 20 10C20 10 16.25 16.875 10 16.875C3.75 16.875 0 10 0 10ZM10 14.375C11.1603 14.375 12.2731 13.9141 13.0936 13.0936C13.9141 12.2731 14.375 11.1603 14.375 10C14.375 8.83968 13.9141 7.72688 13.0936 6.90641C12.2731 6.08594 11.1603 5.625 10 5.625C8.83968 5.625 7.72688 6.08594 6.90641 6.90641C6.08594 7.72688 5.625 8.83968 5.625 10C5.625 11.1603 6.08594 12.2731 6.90641 13.0936C7.72688 13.9141 8.83968 14.375 10 14.375Z"
                                    fill="#20A8E0"
                                  />
                                </svg>
                              </IconButton>
                            </Box>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClickInternal(rowIndex);
                            }}
                          >
                            {expandedRow === rowIndex ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </Box>
                      ) : profileImageColumn &&
                        header.key === profileImageColumn ? (
                        <Tooltip
                          title={String(row[header.key] || "")}
                          disableHoverListener={
                            String(row[header.key] || "").length < 20
                          }
                        >
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {row.fullData?.profileImage ? (
                              <ProfileImage
                                src={
                                  typeof row.fullData.profileImage === "string"
                                    ? row.fullData.profileImage
                                    : URL.createObjectURL(
                                      row.fullData.profileImage,
                                    )
                                }
                                alt={`${row[header.key]} profile`}
                              />
                            ) : (
                              <ProfileIcon />
                            )}
                            {translateCellValue(row[header.key], t) || ""}
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={String(row[header.key] || "")}
                          disableHoverListener={
                            String(row[header.key] || "").length < 20
                          }
                        >
                          <span>{translateCellValue(row[header.key], t) || ""}</span>
                        </Tooltip>
                      )}
                    </ResponsiveTableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={filteredHeaders.length}
                  >
                    <Collapse
                      in={expandedRow === rowIndex}
                      timeout="auto"
                      unmountOnExit
                    >
                      {renderExpandedRow
                        ? renderExpandedRow(row, rowIndex)
                        : renderDefaultExpandedContent(row)}
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <ResponsiveTableCell
                colSpan={filteredHeaders.length}
                sx={{ textAlign: "center", height: "100px" }}
              >
                {t("No data available")}
              </ResponsiveTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </ResponsiveTableContainer>
  );
}
